import * as React from 'react';
import { Component } from 'react';

import Retracked from 'js/app/retracked';
import API from 'js/lib/api';

import { StyleSheet, css } from '@coursera/coursera-ui';
import { SvgArrowLeft } from '@coursera/coursera-ui/svg';

import Submit from 'bundles/authentication/components/Submit';
import { EMAIL_CONSTRAINT } from 'bundles/authentication/constants';
import EmailInput from 'bundles/authentication/shared/EmailInput';
import Form from 'bundles/authentication/shared/Form';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { MODES } from 'bundles/authentication/shared/components/authentication-modal/AuthenticationContent';
import LinkButton from 'bundles/authentication/shared/components/authentication-modal/LinkButton';
import withSingleTracked from 'bundles/common/components/withSingleTracked';

import _t from 'i18n!nls/authentication';

export type Props = {
  className?: string;
  navigate: (path: MODES, options?: $TSFixMe) => void;
  magicLinkRedirect?: string;
};

const TrackedSubmit = withSingleTracked({ type: 'BUTTON' })(Submit);

const api = API('', { type: 'rest' });
const styles = StyleSheet.create({
  container: {
    display: 'grid',
    gridGap: '24px',
    gridTemplateColumns: '1fr',
    margin: '0 auto',
    maxWidth: '396px',
    padding: '32px 16px',
  },
  description: {
    textAlign: 'center',
  },
  hidden: {
    display: 'none',
  },
  form: {
    display: 'grid',
    gridGap: '12px',
    gridTemplateColumns: '1fr',
  },
  navigation: {
    margin: '12px auto 0',
    textAlign: 'center',
    display: 'flex',
  },
  navigationSent: {
    marginTop: '150px',
  },
  navigationIcon: {
    marginRight: '0.6rem',
  },
  section: {
    ':nth-child(1)': {
      '-ms-grid-row': '1',
    },
    ':nth-child(2)': {
      '-ms-grid-row': '2',
    },
    ':nth-child(3)': {
      '-ms-grid-row': '3',
    },
    ':nth-child(4)': {
      '-ms-grid-row': '4',
    },
  },
  title: {
    color: '#1F1F1F',
    fontFamily: 'OpenSans',
    fontSize: '24px',
    lineHeight: '30px',
    margin: '0',
    textAlign: 'center',
    '@media (min-width: 600px)': {
      fontSize: '34px',
      lineHeight: '42px',
    },
  },
  submit: {
    marginTop: '12px',
  },
  separator: {
    margin: '4px 0',
  },
});

class RequestMagicLink extends Component<Props> {
  navigateToLogin = () => {
    const { navigate } = this.props;

    navigate(MODES.LOGIN);
  };

  sendMagicLink = ({ email }: { email: string }) => {
    const { magicLinkRedirect } = this.props;

    api.post('/api/magicLinks.v1/encode', { data: { email, redirectUrl: magicLinkRedirect } });

    this.navigateToMagicLink(email);
  };

  navigateToMagicLink = (email: string) => {
    const { navigate } = this.props;

    navigate(MODES.MAGICLINK, { email });
  };

  render() {
    const { className } = this.props;

    return (
      <section {...css(className, styles.container)} aria-live="polite">
        <header {...css(styles.section)}>
          <h1 {...css(styles.title)}>{_t('Sign in with email')}</h1>
        </header>
        <p {...css(styles.description, styles.section)}>
          {_t('Enter the email address associated with your account, and we’ll send a login link to your inbox.')}
        </p>
        <Form
          {...css(styles.form, styles.section)}
          constraints={{ email: EMAIL_CONSTRAINT }}
          onSubmit={this.sendMagicLink}
        >
          <EmailInput {...css(styles.input, styles.section)} />
          <TrackedSubmit {...css(styles.submit, styles.section)} trackingName="send_login_link_button">
            {_t('Email me a login link')}
          </TrackedSubmit>
        </Form>
        <p {...css(styles.navigation, styles.section)}>
          <LinkButton {...css(styles.navigation)} onClick={this.navigateToLogin}>
            <div {...css(styles.navigationIcon)}>
              <SvgArrowLeft color="#2A73CC" size={15} />
            </div>
            {_t('Back to Log in')}
          </LinkButton>
        </p>
      </section>
    );
  }
}

export default Retracked.createTrackedContainer<Props>(() => ({
  namespace: {
    page: 'magic_link',
  },
}))(RequestMagicLink);
